import React, { useEffect, useState } from "react";
import { Typography, Box } from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid/";
import { WatchActions } from "../../../../state";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";

import {
  format_Word,
  getDetailButton,
  showDisabled,
  showBrandsStatus,
} from "../../../../lib";
import { AddWatchAttribute } from ".";
import { QuickSearchToolbar, escapeRegExp } from "../../../../lib/utils";

// Clickable ON/OFF pill for the Default TPC Sync column. On hover it darkens and
// lifts slightly to signal it's clickable.
function TpcToggleButton({ on, onToggle }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onToggle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        cursor: "pointer",
        border: "none",
        borderRadius: 12,
        padding: "3px 16px",
        fontSize: 12,
        fontWeight: 600,
        color: "#fff",
        minWidth: 52,
        backgroundColor: hover ? (on ? "#1b5e20" : "#6d6d6d") : (on ? "#2e7d32" : "#9e9e9e"),
        transform: hover ? "scale(1.06)" : "scale(1)",
        boxShadow: hover ? "0 2px 6px rgba(15,23,42,0.16)" : "none",
        transition: "background-color 300ms, transform 300ms, box-shadow 300ms",
      }}
    >
      {on ? "ON" : "OFF"}
    </button>
  );
}

const useStyles = makeStyles((theme) => ({
  dataGrid: {
    '& .MuiDataGrid-row': {
      '&:hover': {
        backgroundColor: 'rgba(63, 81, 181, 0.04)',
      },
    },
    '& .MuiDataGrid-cell': {
      borderBottom: '1px solid rgba(224, 224, 224, 0.4)',
      fontSize: '0.875rem',
    },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#fafafa',
      borderBottom: '2px solid rgba(224, 224, 224, 1)',
      fontSize: '0.875rem',
      fontWeight: 600,
    },
    '& .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
  },
}));
export function WatchAttributesAffiliated(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    pageRoute: {
      params: { attribute },
    },
  } = props;
  const siteId = useSelector((state) => state.scopes.site_id);
  const [hide, setHide] = useState(false);
  const [attrRows, setAttrRows] = useState([]);
  const [brandAffiliated, setBrandAffiliated] = useState(false);
  const [groupAffiliated, setGroupAffiliated] = useState(false);
  const [materialAffiliated, setMaterialAffiliated] = useState(false);
  const [dialAffiliated, setDialAffiliated] = useState(false);
  const [handsAffiliated, setHandsAffiliated] = useState(false);
  const [hidden, setHidden] = useState();
  const [deactivateBrandPage, setDeactivateBrandPage] = useState();
  const [hasTpcShare, setHasTpcShare] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const attrData = useSelector(
    (state) => state.watches.attributeList,
    shallowEqual,
  );
  useEffect(() => {
    (async function () {
      if (siteId === undefined) return;
      
      if (attribute === "series_group") {
        await dispatch(WatchActions.getAttributeGroups(attribute, siteId));
      } else if (attribute !== "series") {
        // series is fetched by its own paged/searched effect below
        await dispatch(WatchActions.getAttribute(attribute, null, siteId));
      }
    })();
  }, [attribute, siteId, refreshTrigger, dispatch]);  // Add dispatch to dependencies
  useEffect(() => {
    setHidden(attrData[0]?.hasOwnProperty("hide"));
    setDeactivateBrandPage(attrData[0]?.hasOwnProperty("deactivate_brand"));
    setHasTpcShare(attrData[0]?.hasOwnProperty("tpc_sync_default"));
    setBrandAffiliated(
      attribute === "series_group" ||
      attribute === "series" ||
      attribute === "band_name" ||
      attribute === "clasp_name" ||
      attribute === "clasp_characteristic" ||
      (attrData[0]?.hasOwnProperty("brand_id") &&
        !attrData[0]?.hasOwnProperty("deactivate_brand")),
    );
    setGroupAffiliated(
      attrData[0]?.hasOwnProperty("item_" + attribute + "_groups"),
    );
    setMaterialAffiliated(attrData[0]?.hasOwnProperty("button_material_count"));
    setDialAffiliated(attrData[0]?.hasOwnProperty("dial_color_id"));
    setHandsAffiliated(attrData[0]?.hasOwnProperty("hands_material_id"));
    // Series searches server-side, so a 0-result search returns an empty attrData —
    // clear the rows instead of leaving the previous (stale) list on screen.
    if (attribute === "series" && !attrData.length) {
      setAttrRows([]);
      return;
    }
    if (attrData[0]?.hasOwnProperty(`${attribute}_id`)) {
      const rows = attrData.map((row) => {
        let id = row[`${attribute}_id`];

        var tableRow = {
          id: id,
          button: row,
          name: row[attribute],
          count: row.count,
          caseMaterialCount: null,
          crownMaterialCount: null,
          buttonMaterialCount: null,
          bezelMaterialCount: null,
          claspMaterialCount: null,
          handsMaterialCount: null,
          secondsHandMaterialCount: null,
          hourMarkerMaterialCount: null,
          dialColorCount: null,
          dialFlangeColorCount: null,
          subdialColorCount: null,
          category: attribute === "case_shape" ? (row.category || "") : null,
          brand: null,
          group: null,
          hidden: null,
          status: null,
          tpcShare: null,
        };
        if (hidden) {
          tableRow.hidden = row.hide;
        }
        if (deactivateBrandPage) {
          tableRow.status = row.deactivate_brand;
        }
        if (hasTpcShare) {
          tableRow.tpcShare = row.tpc_sync_default;
        }

        if (brandAffiliated) {
          if (row.item_brand) {
            tableRow.brand = row.item_brand.brand;
          }
        }

        if (groupAffiliated) {
          if (row[`item_${attribute}_groups`][0]) {
            tableRow.group =
              row[`item_${attribute}_groups`][0][`${attribute}_group`];
          }
        }

        if (materialAffiliated) {
          tableRow.count = null;
          tableRow.caseMaterialCount = row.case_material_count;
          tableRow.crownMaterialCount = row.crown_material_count;
          tableRow.buttonMaterialCount = row.button_material_count;
          tableRow.bezelMaterialCount = row.bezel_material_count;
          tableRow.claspMaterialCount = row.clasp_material_count;
        }
        if (handsAffiliated) {
          tableRow.count = null;
          tableRow.handsMaterialCount = row.hands_material_count;
          tableRow.secondsHandMaterialCount = row.second_hand_material_count;
          tableRow.hourMarkerMaterialCount = row.hour_marker_material_count;
        }
        if (dialAffiliated) {
          tableRow.count = null;
          tableRow.dialColorCount = row.dial_color_count;
          tableRow.dialFlangeColorCount = row.dial_flange_color_count;
          tableRow.subdialColorCount = row.subdial_color_count;
        }

        return tableRow;
      });
      setAttrRows(rows);
    }
  }, [
    attrData,
    attribute,
    brandAffiliated,
    deactivateBrandPage,
    dialAffiliated,
    groupAffiliated,
    handsAffiliated,
    hidden,
    hasTpcShare,
    materialAffiliated,
  ]);
  const attrColumns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.3,
      headerAlign: "center",
      align: "center",
    },
    { field: "name", headerName: "Name", flex: 1 },
    {
      field: "count",
      headerName: "Count",
      type: "number",
      flex: 0.4,
      headerAlign: "left",
      align: "left",
      hide: materialAffiliated || handsAffiliated || dialAffiliated,
      filterable: !materialAffiliated || !handsAffiliated || !dialAffiliated,
    },
    {
      field: "caseMaterialCount",
      headerName: "Case Material Count",
      type: "number",
      flex: 0.4,
      headerAlign: "left",
      align: "left",
      hide: !materialAffiliated,
      filterable: materialAffiliated,
    },
    {
      field: "crownMaterialCount",
      headerName: "Crown Material Count",
      type: "number",
      flex: 0.4,
      headerAlign: "left",
      align: "left",
      hide: !materialAffiliated,
      filterable: materialAffiliated,
    },
    {
      field: "buttonMaterialCount",
      headerName: "Button Material Count",
      type: "number",
      flex: 0.4,
      headerAlign: "left",
      align: "left",
      hide: !materialAffiliated,
      filterable: materialAffiliated,
    },
    {
      field: "bezelMaterialCount",
      headerName: "Bezel Material Count",
      type: "number",
      flex: 0.4,
      headerAlign: "left",
      align: "left",
      hide: !materialAffiliated,
      filterable: materialAffiliated,
    },
    {
      field: "claspMaterialCount",
      headerName: "Clasp Material Count",
      type: "number",
      flex: 0.4,
      headerAlign: "left",
      align: "left",
      hide: !materialAffiliated,
      filterable: materialAffiliated,
    },
    {
      field: "handsMaterialCount",
      headerName: "Hands Material Count",
      type: "number",
      flex: 0.4,
      headerAlign: "left",
      align: "left",
      hide: !handsAffiliated,
      filterable: handsAffiliated,
    },
    {
      field: "secondsHandMaterialCount",
      headerName: "Seconds Hand Material Count",
      type: "number",
      flex: 0.4,
      headerAlign: "left",
      align: "left",
      hide: !handsAffiliated,
      filterable: handsAffiliated,
    },
    {
      field: "hourMarkerMaterialCount",
      headerName: "Hour Marker Material Count",
      type: "number",
      flex: 0.4,
      headerAlign: "left",
      align: "left",
      hide: !handsAffiliated,
      filterable: handsAffiliated,
    },
    {
      field: "dialColorCount",
      headerName: "Dial Color Count",
      type: "number",
      flex: 0.4,
      headerAlign: "left",
      align: "left",
      hide: !dialAffiliated,
      filterable: dialAffiliated,
    },
    {
      field: "dialFlangeColorCount",
      headerName: "Dial Flange Color Count",
      type: "number",
      flex: 0.4,
      headerAlign: "left",
      align: "left",
      hide: !dialAffiliated,
      filterable: dialAffiliated,
    },
    {
      field: "subdialColorCount",
      headerName: "SubDial Color Count",
      type: "number",
      flex: 0.4,
      headerAlign: "left",
      align: "left",
      hide: !dialAffiliated,
      filterable: dialAffiliated,
    },
    {
      field: "category",
      headerName: "Category",
      flex: 0.6,
      hide: attribute !== "case_shape",
      filterable: attribute === "case_shape",
      renderCell: (params) => {
        const map = { "Width+Height": "Width & Height" };
        return map[params.value] || params.value || "";
      },
    },
    {
      field: "brand",
      headerName: "Brand Affiliation",
      flex: 1,
      hide: !brandAffiliated,
      filterable: brandAffiliated,
    },
    {
      field: "group",
      headerName: "Group",
      flex: 1,
      hide: !groupAffiliated,
      filterable: groupAffiliated,
    },
    {
      field: "hidden",
      headerName: "Hidden",
      flex: 0,
      hide: !hidden,
      filterable: false,
      renderCell: (params) => {
        return showDisabled(params.value);
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.3,
      hide: !deactivateBrandPage,
      filterable: false,
      renderCell: (params) => {
        return showBrandsStatus(!params.value);
      },
    },
    {
      field: "button",
      headerName: "Details",
      width: 90,
      hide: hide,
      filterable: false,
      renderCell: (params) => {
        let path = "watch-details";
        if (attribute === "series_group") {
          path = "watch-details-groups";
        } else if (materialAffiliated || dialAffiliated || handsAffiliated) {
          path = "watch-details-tabs";
        } else {
          path = "watch-details";
        }
        return getDetailButton(
          path,
          params.value,
          attribute,
          brandAffiliated,
          siteId,
        );
      },
    },
    {
      field: "tpcShare",
      headerName: "Default TPC Sync",
      width: 150,
      hide: !hasTpcShare,
      filterable: false,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const on = !!params.row.tpcShare;
        return (
          <TpcToggleButton
            on={on}
            onToggle={async () => {
              const ok = await dispatch(
                WatchActions.toggleBrandTpcSync(params.row.id, on ? 0 : 1),
              );
              if (ok) setRefreshTrigger((prev) => prev + 1);
            }}
          />
        );
      },
    },
  ];

  let isCaseShape = false;
  if (attribute === "case_shape") {
    isCaseShape = true;
  }
  const [searchText, setSearchText] = useState("");
  const [rows, setRows] = useState(attrRows);
  const isSeries = attribute === "series";
  // Series is server-side paged + searched. page is 0-indexed (MUI v4 DataGrid),
  // pageSize 100; `total` drives the pagination arrows so all ~4k are reachable.
  const SERIES_PAGE_SIZE = 100;
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
    // Series searches on the SERVER (see the effects below); other attributes stay
    // client-side and filter the already-loaded rows.
    if (isSeries) return;
    const searchRegex = new RegExp(escapeRegExp(searchValue), "i");
    const filteredRows = attrRows.filter((row) => {
      return Object.keys(row).some((field) => {
        return searchRegex.test(row[field]?.toString());
      });
    });
    setRows(filteredRows);
  };
  // Series: debounce the typed term into debouncedSearch and jump to page 0 on a
  // new search so results start from the top.
  useEffect(() => {
    if (!isSeries) return;
    const t = setTimeout(() => {
      setPage(0);
      setDebouncedSearch(searchText);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);
  // Series: fetch the current page whenever the search, page, or a refresh changes,
  // and capture `total` from the response to drive the pagination arrows.
  useEffect(() => {
    if (!isSeries || siteId === undefined) return;
    (async () => {
      const data = await dispatch(
        WatchActions.getAttribute("series", null, siteId, debouncedSearch, page),
      );
      if (data && typeof data.total === "number") setTotal(data.total);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, page, refreshTrigger, siteId]);
  useEffect(() => {
    // Series always mirrors the server result (even an empty page); other
    // attributes keep the length guard so an empty list doesn't blank the grid.
    if (isSeries || attrRows?.length) {
      setRows(attrRows);
    }
  }, [attrRows, isSeries]);
  return (
    <Box style={{ width: "100%" }}>
      <Box display="flex" justifyContent="center" mb={2}>
        <Typography variant="h6">{format_Word(attribute)}</Typography>
      </Box>
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <AddWatchAttribute
          attribute={format_Word(attribute)}
          rawAttribute={attribute}
          brandAffiliated={brandAffiliated}
          isCaseShape={isCaseShape}
          path={"watch-attribute-affiliated"}
          onSuccess={() => setRefreshTrigger(prev => prev + 1)}
        />
      </div>
      <div style={{ maxWidth: attribute === "case_material" ? 1400 : 1300, margin: "0 auto" }}>
        <DataGrid
          className={classes.dataGrid}
          rows={rows}
          columns={attrColumns}
          disableColumnMenu={true}
          isRowSelectable={false}
          pageSize={SERIES_PAGE_SIZE}
          {...(isSeries
            ? {
                paginationMode: "server",
                rowCount: total,
                page: page,
                onPageChange: (param) => setPage(param.page),
                rowsPerPageOptions: [SERIES_PAGE_SIZE],
              }
            : {})}
          checkboxSelection={false}
          autoHeight={true}
          rowHeight={38}
          sortModel={[
            {
              field: "name",
              sort: "asc",
            },
          ]}
          components={{ Toolbar: QuickSearchToolbar }}
          componentsProps={{
            toolbar: {
              value: searchText,
              onChange: (event) => {
                requestSearch(event.target.value);
              },
              clearSearch: () => requestSearch(""),
            },
          }}
        />
      </div>
    </Box>
  );
}
